import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import liveNotificationService from '../services/liveNotificationService';

const useAuctionNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollHandleRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    const { data } = await liveNotificationService?.getNotifications(user?.id);
    setNotifications(data || []);
    setUnreadCount((data || [])?.filter(n => !n?.is_read)?.length);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    // Stop any existing poll before starting a new one
    if (pollHandleRef?.current) {
      liveNotificationService?.unsubscribe(pollHandleRef?.current);
      pollHandleRef.current = null;
    }

    loadNotifications();

    // Start polling for new notifications every 3 seconds
    pollHandleRef.current = liveNotificationService?.subscribeToNotifications(
      user?.id,
      (newNotification) => {
        setNotifications(prev => [newNotification, ...prev]);
        setUnreadCount(prev => prev + 1);
      }
    );

    return () => {
      if (pollHandleRef?.current) {
        liveNotificationService?.unsubscribe(pollHandleRef?.current);
        pollHandleRef.current = null;
      }
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const markAsRead = useCallback(async (notificationId) => {
    await liveNotificationService?.markAsRead(notificationId);
    setNotifications(prev =>
      prev?.map(n => n?.id === notificationId ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;
    await liveNotificationService?.markAllAsRead(user?.id);
    setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  }, [user?.id]);

  return { notifications, unreadCount, loading, markAsRead, markAllAsRead, reload: loadNotifications };
};

export default useAuctionNotifications;
